/**
 * MinhaConta.js — a conta de quem está logado (Onda 150)
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * Qualquer usuário, de qualquer perfil, vê os próprios dados e troca a própria senha. Nasceu para
 * os gestores do Ipê, que recebem do administrador uma senha padrão e devem trocá-la. A rota é a
 * POST /api/auth/change-password, que confere a senha atual, aplica as regras da senha nova e
 * registra a troca na auditoria.
 */
import React, { useState } from 'react';
import { FiEye, FiEyeOff, FiKey, FiCheckCircle } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/MinhaConta.css';

const MIN = 8;

function regras(nova, atual, usuario) {
  const s = String(nova || '');
  return [
    { ok: s.length >= MIN, txt: `Pelo menos ${MIN} caracteres` },
    { ok: /[A-Za-zÀ-ÿ]/.test(s) && /[0-9]/.test(s), txt: 'Letras e números' },
    { ok: s.length > 0 && s !== atual, txt: 'Diferente da senha atual' },
    { ok: s.length > 0 && s.toLowerCase() !== String(usuario || '').toLowerCase(), txt: 'Diferente do nome de usuário' },
  ];
}

function fmtData(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function CampoSenha({ id, rotulo, valor, onChange, autoComplete }) {
  const [ver, setVer] = useState(false);
  return (
    <div className="mc-campo">
      <label htmlFor={id}>{rotulo}</label>
      <div className="mc-senha">
        <input id={id} type={ver ? 'text' : 'password'} value={valor} onChange={e => onChange(e.target.value)}
          autoComplete={autoComplete} maxLength={128} />
        <button type="button" className="mc-olho" onClick={() => setVer(v => !v)}
          aria-label={ver ? `Ocultar ${rotulo.toLowerCase()}` : `Mostrar ${rotulo.toLowerCase()}`}>
          {ver ? <FiEyeOff /> : <FiEye />}
        </button>
      </div>
    </div>
  );
}

export default function MinhaConta() {
  const { user } = useAuth();
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirma, setConfirma] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [feito, setFeito] = useState(false);

  const lista = regras(nova, atual, user?.username);
  const confere = confirma.length > 0 && confirma === nova;
  const pronto = atual.length > 0 && lista.every(r => r.ok) && confere && !salvando;

  async function salvar(e) {
    e.preventDefault();
    if (!pronto) return;
    setSalvando(true); setErro(''); setFeito(false);
    try {
      await api.post('/api/auth/change-password', { currentPassword: atual, newPassword: nova });
      setAtual(''); setNova(''); setConfirma('');
      setFeito(true);
    } catch (err) {
      setErro(err.response?.data?.error || 'Não foi possível alterar a senha. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="mc-pagina">
      <header className="mc-cabecalho">
        <h1>Minha conta</h1>
        <p>Seus dados de acesso e a troca da sua senha.</p>
      </header>

      <section className="mc-cartao" aria-labelledby="mc-dados">
        <h2 id="mc-dados">Seus dados</h2>
        <dl className="mc-dados">
          <div><dt>Usuário</dt><dd>{user?.username || '—'}</dd></div>
          <div><dt>E-mail</dt><dd>{user?.email || '—'}</dd></div>
          <div><dt>Perfil</dt><dd>{user?.role_label || user?.role || '—'}</dd></div>
          <div><dt>Último acesso</dt><dd>{fmtData(user?.last_login_at)}</dd></div>
        </dl>
        <p className="mc-nota">Para mudar usuário, e-mail ou perfil, fale com o administrador da plataforma.</p>
      </section>

      <section className="mc-cartao" aria-labelledby="mc-troca">
        <h2 id="mc-troca"><FiKey aria-hidden="true" /> Alterar senha</h2>
        <form onSubmit={salvar} className="mc-form" noValidate>
          <CampoSenha id="mc-atual" rotulo="Senha atual" valor={atual} onChange={v => { setAtual(v); setFeito(false); }} autoComplete="current-password" />
          <CampoSenha id="mc-nova" rotulo="Nova senha" valor={nova} onChange={v => { setNova(v); setFeito(false); }} autoComplete="new-password" />
          <ul className="mc-regras" aria-label="Regras da nova senha">
            {lista.map(r => (
              <li key={r.txt} className={r.ok ? 'mc-ok' : ''}>{r.ok ? '✓' : '•'} {r.txt}</li>
            ))}
          </ul>
          <CampoSenha id="mc-confirma" rotulo="Confirme a nova senha" valor={confirma} onChange={v => { setConfirma(v); setFeito(false); }} autoComplete="new-password" />
          {confirma.length > 0 && !confere && <p className="mc-aviso">A confirmação não é igual à nova senha.</p>}
          {erro && <p className="mc-erro" role="alert">{erro}</p>}
          {feito && <p className="mc-sucesso" role="status"><FiCheckCircle aria-hidden="true" /> Senha alterada. Use a nova senha no próximo acesso.</p>}
          <div className="mc-acoes">
            <button type="submit" className="btn btn-primary" disabled={!pronto}>{salvando ? 'Salvando…' : 'Alterar senha'}</button>
          </div>
        </form>
      </section>
    </div>
  );
}
